/**
 * The 📊 beside the title — every count this import has, on demand.
 *
 * It exists because the header lost three cards on 2026-08-26 (Date, Contact
 * list title, Contacts) and one of them held real numbers. The trade is only
 * a good one because **the headline count did not actually move**: the pager
 * under the table already reads `1–10 of 14`, so "how many contacts" is still
 * answered without a click. What moves here is the long tail that had no home
 * — exported, new positions, skipped, failed, conflicts — plus the import
 * date, which was a whole card for a value nobody reads twice.
 *
 * Derived counts render only when NON-ZERO, the rule the Contacts card
 * already used: "0 exported" is noise on a list nobody has exported. Contacts
 * and Imported are unconditional — "0 contacts" on an empty import is
 * information.
 *
 * Opens on CLICK, not hover (`/pr-review` iter 1, Agent C I2). Seven rows of
 * numbers in a `pointer-events-none` hover bubble cannot be selected, copied,
 * or read on touch, and they vanish the moment the pointer drifts — the same
 * argument `FullHistory` makes for its own trail. It also made `exported-count`
 * invisible to Playwright, which broke `export.spec.ts`'s durable-after-reload
 * assertion.
 */
import type { ExportStatus, ImportDetail } from "@nu-assist/contracts";

import { IconPopover } from "./IconPopover";

/** `2026-08-01T10:00:00Z` -> `8/1/2026`, and never `Invalid Date`.
 *
 * A malformed `created_at` should degrade to an em-dash, not print the string
 * "Invalid Date" into the panel (`/pr-review` iter 1, Agent C minor). */
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function Row({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: string;
  tone?: string;
  testId?: string;
}) {
  return (
    <span className="flex items-baseline justify-between gap-6">
      <span className="text-text-secondary">{label}</span>
      <span data-testid={testId} className={tone ?? "text-text"}>
        {value}
      </span>
    </span>
  );
}

export function ImportStatsPopover({
  detail,
  exportStatus,
}: {
  detail: ImportDetail;
  /** Live export poll — same precedence the Contacts card used. */
  exportStatus?: ExportStatus;
}) {
  const conflicts = detail.conflict_count ?? 0;
  const exported = exportStatus?.exported_count ?? detail.exported_count ?? 0;
  const newPositions = exportStatus?.new_positions_count ?? detail.new_positions_count ?? 0;
  const skipped = detail.skipped_count ?? 0;
  const failed = detail.failed_count ?? 0;
  // Rows the parser dropped for want of a `/in/` LinkedIn URL (2026-09-28).
  // `null` is an import parsed before the rule; `0` is "checked, none" —
  // both stay quiet, like every other derived count here.
  const skippedNoLinkedin = detail.skipped_no_linkedin ?? 0;
  // Rows a Thrive pull KEPT without a LinkedIn URL (04): in the list, but out
  // of the person store, Enrich and People search. Not the row above, which
  // counts rows an upload dropped.
  const withoutLinkedin = detail.without_linkedin_count ?? 0;

  return (
    <IconPopover
      label="People list statistics"
      triggerTestId="import-stats-trigger"
      panelTestId="import-stats"
      panelClassName="max-w-[280px]"
      icon={
        /* Bar-chart glyph, inline SVG so it inherits `currentColor` and needs
           no icon dependency. `aria-hidden` — the button carries the name. */
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        >
          <path d="M2 14h12" />
          <path d="M4.5 14V9" />
          <path d="M8 14V4" />
          <path d="M11.5 14v-7" />
        </svg>
      }
    >
      <span className="flex flex-col gap-1 text-left text-body-sm">
        <Row label="Imported" value={formatDate(detail.created_at)} />
        <Row
          label="Contacts"
          value={detail.total_contacts.toLocaleString()}
          testId="contacts-count"
        />
        {exported > 0 && (
          <Row
            label="Exported"
            value={exported.toLocaleString()}
            tone="text-success-ink"
            testId="exported-count"
          />
        )}
        {newPositions > 0 && (
          <Row
            label="New positions"
            value={newPositions.toLocaleString()}
            tone="text-accent-ink"
            testId="new-positions-count"
          />
        )}
        {skipped > 0 && <Row label="Skipped" value={skipped.toLocaleString()} />}
        {skippedNoLinkedin > 0 && (
          <Row
            label="Skipped: no LinkedIn URL"
            value={skippedNoLinkedin.toLocaleString()}
            tone="text-warning-ink"
            testId="skipped-no-linkedin-count"
          />
        )}
        {withoutLinkedin > 0 && (
          <Row
            label="Without LinkedIn URL"
            value={withoutLinkedin.toLocaleString()}
            tone="text-warning-ink"
            testId="without-linkedin-count"
          />
        )}
        {failed > 0 && <Row label="Failed" value={failed.toLocaleString()} tone="text-error-ink" />}
        {conflicts > 0 && (
          <Row
            // "To review", matching the pill on the list and the chip on the
            // table below. One population, one name — the three used to say
            // "conflicts", "conflicts" and "To review" for it, and the first two
            // also counted it differently.
            label="To review"
            value={conflicts.toLocaleString()}
            tone="text-warning-ink"
            testId="conflicts-count"
          />
        )}
      </span>
    </IconPopover>
  );
}
